/**
 * Root application component.
 *
 * Task 21.1 wires the SPA auth flow: the tree is wrapped in {@link AuthProvider}
 * and gated on authentication. Unauthenticated users see the {@link SignIn}
 * view (credentials → OTP); authenticated users see the app shell.
 *
 * Task 21.2 fills the authenticated shell with the app views — accounts &
 * transactions (with reversal), beneficiaries, transfers, spend insights,
 * reversal by id, and notifications.
 *
 * Presentation redesign: the shell is now a fintech dashboard — a fixed
 * left sidebar (brand + vertical nav + user/sign-out) and a full-width main
 * content column. The navigation is a real `<nav>` with `aria-current="page"`
 * on the active item (keyboard-operable buttons with visible focus rings). A
 * small `onNavigate` callback is threaded into the dashboard so the quick-action
 * tiles can switch sections. None of the auth flow, provider, gate, data, or
 * view logic changed — only the surrounding markup/classNames.
 */

import { useState } from 'react';

import { AuthProvider, SignIn, useAuth } from './auth/index.js';
import {
  AccountsView,
  BeneficiariesView,
  InsightsView,
  NotificationsView,
  ReversalView,
  TransfersView,
} from './views/index.js';
import {
  BrandLogo,
  IconBell,
  IconDashboard,
  IconInsights,
  IconPeople,
  IconReverse,
  IconTransfer,
} from './ui/BrandLogo.js';
import { ChatWidget } from './ui/ChatWidget.js';
import './ui/styles.css';

/** The selectable sections of the authenticated app shell. */
type TabId =
  | 'accounts'
  | 'beneficiaries'
  | 'transfers'
  | 'insights'
  | 'reversal'
  | 'notifications';

/** Nav item descriptor: id, label, and its inline-SVG icon. */
interface NavItem {
  readonly id: TabId;
  readonly label: string;
  readonly Icon: (props: { readonly className?: string }) => JSX.Element;
}

const NAV_ITEMS: readonly NavItem[] = [
  { id: 'accounts', label: 'Dashboard', Icon: IconDashboard },
  { id: 'beneficiaries', label: 'Beneficiaries', Icon: IconPeople },
  { id: 'transfers', label: 'Transfers', Icon: IconTransfer },
  { id: 'insights', label: 'Insights', Icon: IconInsights },
  { id: 'reversal', label: 'Reverse', Icon: IconReverse },
  { id: 'notifications', label: 'Notifications', Icon: IconBell },
];

/** Render the active section's view. `onNavigate` lets the dashboard's
 * quick-action tiles jump to another section. */
function renderSection(active: TabId, onNavigate: (id: TabId) => void): JSX.Element {
  switch (active) {
    case 'accounts':
      return <AccountsView onNavigate={onNavigate} />;
    case 'beneficiaries':
      return <BeneficiariesView />;
    case 'transfers':
      return <TransfersView />;
    case 'insights':
      return <InsightsView />;
    case 'reversal':
      return <ReversalView />;
    case 'notifications':
      return <NotificationsView />;
    default:
      return <AccountsView onNavigate={onNavigate} />;
  }
}

/** The authenticated app shell: sidebar navigation + full-width content. */
function AppShell(): JSX.Element {
  const { displayName, signOut, pending } = useAuth();
  const [active, setActive] = useState<TabId>('accounts');

  return (
    <div className="esb-shell">
      <aside className="esb-sidebar" aria-label="Primary">
        <div className="esb-sidebar__brand">
          <BrandLogo />
        </div>

        <nav className="esb-sidenav" aria-label="Sections">
          <ul className="esb-sidenav__list">
            {NAV_ITEMS.map(({ id, label, Icon }) => {
              const isActive = id === active;
              return (
                <li key={id}>
                  <button
                    type="button"
                    className={`esb-sidenav__item${isActive ? ' esb-sidenav__item--active' : ''}`}
                    aria-current={isActive ? 'page' : undefined}
                    onClick={() => setActive(id)}
                  >
                    <Icon className="esb-sidenav__icon" />
                    <span className="esb-sidenav__label">{label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="esb-sidebar__footer">
          <div className="esb-userchip">
            <span className="esb-userchip__avatar" aria-hidden="true">
              {(displayName ?? '?').slice(0, 1).toUpperCase()}
            </span>
            <span className="esb-userchip__meta">
              <span className="esb-userchip__label">Signed in as</span>
              <span className="esb-userchip__id" title={displayName}>{displayName}</span>
            </span>
          </div>
          <button
            type="button"
            className="esb-btn esb-btn--ghost esb-btn--sm esb-signout"
            onClick={() => void signOut()}
            disabled={pending}
          >
            Sign out
          </button>
        </div>
      </aside>

      <main className="esb-main">
        <div className="esb-main__inner">{renderSection(active, setActive)}</div>
      </main>

      {/* Floating assistant — available over every section without navigation. */}
      <ChatWidget />
    </div>
  );
}

/**
 * Chooses what to render based on auth status. Kept separate from
 * {@link App} so it can call {@link useAuth} from inside the provider.
 */
function AppContent(): JSX.Element {
  const { status } = useAuth();

  if (status === 'initializing') {
    // The one-time silent refresh is resolving; avoid flashing the sign-in form.
    return (
      <div className="esb-boot" aria-busy="true">
        <div className="esb-boot__card">
          <BrandLogo />
          <div className="esb-boot__spinner" aria-hidden="true" />
          <p className="esb-boot__text">Getting things ready…</p>
        </div>
      </div>
    );
  }

  if (status !== 'authenticated') {
    return <SignIn />;
  }

  return <AppShell />;
}

export function App(): JSX.Element {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
